import { evaluate } from "mathjs";

import type { MaterialFormulaVariable } from "../types";

const FORMULA_BUILTIN_NAMES = new Set([
    "ceil",
    "floor",
    "round",
    "abs",
    "min",
    "max",
    "sqrt",
    "pow",
    "true",
    "false",
    "equalText",
    "compareText",
]);

export function evaluateFormula(expression: string, scope: any): number {
    if (!expression?.trim()) return 0;

    try {
        const result = evaluate(expression, scope);
        const num = Number(result);
        return isNaN(num) ? 0 : roundToTwoDecimals(num);
    } catch {
        return 0;
    }
}

export function getFormulaVariables(
    expression: string,
    scope: Record<string, any>,
): MaterialFormulaVariable[] {
    if (!expression?.trim()) return [];

    const identifiers = extractFormulaIdentifiers(expression);
    const seen = new Set<string>();
    const variables: MaterialFormulaVariable[] = [];

    for (const name of identifiers) {
        if (!(name in scope) || seen.has(name)) continue;
        seen.add(name);
        variables.push({ name, value: scope[name] });
    }

    return variables.sort((a, b) => a.name.localeCompare(b.name));
}

export function substituteFormulaExpression(
    expression: string,
    scope: Record<string, any>,
): string {
    const variables = getFormulaVariables(expression, scope);
    let substituted = expression;

    for (const { name, value } of [...variables].sort(
        (a, b) => b.name.length - a.name.length,
    )) {
        substituted = substituted.replace(
            new RegExp(`\\b${escapeRegExp(name)}\\b`, "g"),
            formatFormulaValue(value),
        );
    }

    return substituted;
}

function extractFormulaIdentifiers(expression: string): string[] {
    const matches = expression.match(/\b[A-Za-z_][A-Za-z0-9_]*\b/g) ?? [];
    const seen = new Set<string>();
    const identifiers: string[] = [];

    for (const name of matches) {
        if (FORMULA_BUILTIN_NAMES.has(name) || seen.has(name)) continue;
        seen.add(name);
        identifiers.push(name);
    }

    return identifiers;
}

function formatFormulaValue(value: number | string): string {
    const num = Number(value);
    if (!Number.isNaN(num) && Number.isFinite(num)) {
        return Number.isInteger(num)
            ? String(num)
            : String(roundToTwoDecimals(num));
    }
    return JSON.stringify(value);
}

function escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function roundToTwoDecimals(value: number): number {
    if (!Number.isFinite(value)) return 0;
    return Math.round(value * 100) / 100;
}
